import type { CSSProperties, FC } from "react";

import classNames from "classnames";
import Link from "next/link";

import { AnchorDownIcon } from "@jti/brand-icons/anchor-down";
import { getOptimizedBackgroundImage } from "@jti/ui/utils/getOptimizedBackgroundImage";

import type { anchorPushInputs } from "./AnchorPush.builder";

import styles from "./AnchorPush.module.scss";

type AnchorPushProps = BuilderComponentProps<typeof anchorPushInputs>;

const getBackgroundImage = (src?: string) => (src ? getOptimizedBackgroundImage(src) : "none");

export const AnchorPush: FC<AnchorPushProps> = ({
  isAnchorIconEnabled,
  isBackgroundOverlayEnabled,
  isBackgroundImageEnabled,
  isGradientEnabled,
  content,
  theme,
}) => {
  return (
    <div
      className={classNames(styles.wrapperAnchor, {
        "light-theme": theme === "light",
        "dark-theme": theme === "dark",
      })}
    >
      {content.map(({ img, imgTablet, imgMobile, anchorId, text, altText }) => {
        const href = anchorId.startsWith("/") ? anchorId : `#${anchorId}`;
        const desktopImage = getBackgroundImage(img);
        const tabletImage = getBackgroundImage(imgTablet || img);
        const mobileImage = getBackgroundImage(imgMobile || imgTablet || img);

        return (
          <Link
            href={href}
            data-testid={anchorId}
            className={classNames(styles.anchorCard, {
              [styles.anchorBackgroundImage as string]: isBackgroundImageEnabled,
            })}
            key={`${anchorId}-${text}`}
          >
            <div className={styles.bottomSection}>
              {isAnchorIconEnabled && <AnchorDownIcon className={styles.anchorIcon} />}
              <span className={styles.anchorText}>{text}</span>
            </div>
            {isBackgroundOverlayEnabled && isBackgroundImageEnabled && <div className={styles.gradientOverlay}></div>}
            {isBackgroundImageEnabled && (
              <div
                role="img"
                aria-label={altText}
                className={styles.backgroundImage}
                style={
                  {
                    "--image-src": desktopImage,
                    "--image-src-tablet": tabletImage,
                    "--image-src-mobile": mobileImage,
                    "--has-gradient": isGradientEnabled ? "var(--gradient-overlay)" : "none",
                  } as CSSProperties
                }
              />
            )}
          </Link>
        );
      })}
    </div>
  );
};
